/* App.tsx
   Version: 1.2.1
   Date: 2021-02-04
*/

import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import './css/icons/css/all.css'
import SideBar, { SideBarEntry } from './appComponents/SideBar';
import { AppContextData, AppProvider } from './AppContext';
import Container from './basicComponents/Container';
import Icon from './basicComponents/Icon';
import PrivateRoute from './basicComponents/PrivateRoute';
import Text from './basicComponents/Text';
import SearchPage, { SearchPageState } from './pageComponents/SearchPage';
import LoginPage from './pageComponents/LoginPage/LoginPageRouter';
import LoggingPage from './pageComponents/LoggingPage';
import { SearchOption, SearchParameter, UserSettingSearchField } from './data/Search';
import HistoryPage from './pageComponents/HistoryPage';
import DialogPage from './pageComponents/DialogPage';
import SettingsPage from './pageComponents/SettingsPage';
import { InitResponse, TestcasesResponse } from './data/Init';
import LogRecord from './data/LogRecord';
import { DateUtil } from './util/DateUtil';
import Message from './basicComponents/Message';
import { LogSearchParameter } from './data/interface';
import { Logging } from './data/Logging';
import LogSearchPage from './pageComponents/LogSearch/LogSearchPage';
import MessageLoader from './basicComponents/MessageLoader';
import MessageBox from './basicComponents/MessageBox';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { User } from '@sympalog/user';
import { RealRestClient, RestClient } from '@sympalog/communication';

export interface RecordNavigation {
	nextLeft: () => LogRecord | undefined;
	nextRight: () => LogRecord | undefined;
	moveToRecord?: (record: LogRecord) => void;
}


// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface AppProps {
}

interface AppState {

	version: string | undefined;
    sidebar: SideBarEntry[];
	status: string;
	checkToken: boolean;
	showDialog: JSX.Element | undefined;
	menu: Node | undefined;

	searchRange: string;
	searchLimit: number;
	logSearchLimit: number;

	searchParameter: SearchParameter;
	records: LogRecord[] | undefined;
    curRecord: LogRecord | undefined;

	logSearchParameter: LogSearchParameter;
	logList: Logging[];

	testcases: string[];

}

export default class App extends React.Component<AppProps, AppState> {

	private static appName = 'SymQuery';

	private ctx: AppContextData;
	private message: Message | null = null;

	private searchPageState: SearchPageState | undefined;
	private recordNavigation: RecordNavigation;

    constructor(props: AppProps) {

        // call super
        super(props);

		// create rest client
		let restAppService: RestClient;
		let restSymDialogService: RestClient;
		if (window.location.port !== '3000') {
			restAppService = new RealRestClient(window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + '/' + App.appName + '/api/');
			restSymDialogService = new RealRestClient(window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + '/SymDialog/api/');
		}
		else {
			// restClient = new RealRestClient('https://localhost:14009/' + App.appName + '/api/');
			// restSymDialog = new RealRestClient('https://localhost:14009/SymDialog/api/');
            restAppService = new RealRestClient('https://rz1.sympalog.net:16009/' + App.appName + '/api/');
            restSymDialogService = new RealRestClient('https://rz1.sympalog.net:16009/SymDialog/api/');
		}

		// create user (use user in SymAdmin package)
		const user = new User('/' + App.appName, restSymDialogService);

		// create context
		this.ctx = { app: this, user: user, restAppService: restAppService, restSymDialogService: restSymDialogService }

		// create record navigation
		this.recordNavigation = {
			nextLeft: this.nextLeft,
			nextRight: this.nextRight,
		}

        // set state
        this.state = { 

			version: undefined,
			sidebar: this.createSidebarEntries(), 
			status: 'check token' ,
			checkToken: true,
			showDialog: undefined,
			menu: undefined,

			searchRange: 'yesterday',
			searchLimit: 1000,
			logSearchLimit: 200,

			searchParameter: {
				serverEntries: [],
				selectedServer: [],
                queryDateFrom: DateUtil.getYesterdayAsString(),
                queryDateTo: DateUtil.getYesterdayAsString(),
                queryTimeFrom: '00:00',
                queryTimeTo: '23:59',
                sessionId: undefined,
                callerId: '',
                cdpn: '',
				configuration: '',
				searchOptions: [],
				filterMap: new Map<string, string>(),
			},

			records: undefined,
			curRecord: undefined,

			logSearchParameter: {
				serverEntries: [],
				selectedServer: [],
                queryDateFrom: DateUtil.getYesterdayAsString(),
                queryDateTo: DateUtil.getYesterdayAsString(),
                queryTimeFrom: '00:00',
                queryTimeTo: '23:59',
				logLevel: 900,
			},
			logList: [],

			testcases: [],

        }

    }

    public render(): JSX.Element {

        return (

			<Container className='frame-screen'>

				<AppProvider ctx={this.ctx}>

					<Container className='frame-header'>
						<a href='/'>
							<img className='frame-header-logo' src={`${process.env.PUBLIC_URL}/image/logo_300.png`} alt='Sympalog Logo' />
						</a>
						<div className='frame-header-space' />
						{
							this.ctx.user.isLoggedIn() ?
								<div className='frame-header-right'>
									<div className='frame-header-right-space' />
									<Icon className='frame-header-right-logout-32' url={`${process.env.PUBLIC_URL}/image/logout1.svg`} name='logout' onClick={this.handleLogout} />
								</div>
								:
								null
						}
					</Container>

					<Container className='frame-body'>

						<Router basename='/SymQuery'>
							<CompatRouter>

							<Container className='frame-sidebar'>
								<SideBar entries={this.state.sidebar} resetSidebar={this.resetSidebar} />
							</Container>

							<Container className='frame-application'>

								<Container className='frame-menu'>
									<Text className='frame-space' />
									<Text className='frame-footer-text-right' >{this.ctx.user.isLoggedIn() ? this.ctx.user.getPackageName() + '/' + this.ctx.user.getUsername() : null}</Text>
								</Container>

								<Container className='frame-content-flex'>
									<Switch >
										<Route path='/login'><LoginPage checkToken={this.state.checkToken} handleLogin={this.handleLogin} /></Route>
										<PrivateRoute path='/search'>
											<SearchPage 
												searchParameter={this.state.searchParameter}
												records={this.state.records}
												curRecord={this.state.curRecord}
												setSearchParameter={this.setSearchParameter}
												setRecords={this.setRecords}
												setCurRecord={this.setCurRecord}
												searchRange={this.state.searchRange}
												searchLimit={this.state.searchLimit}
												/>
										</PrivateRoute>
										<PrivateRoute path='/logsearch'>
											<LogSearchPage 
												logSearchParameter={this.state.logSearchParameter}
												logList={this.state.logList}
												setLogSearchParameter={this.setLogSearchParameter}
												setLogList={this.setLogList}
												searchRange={this.state.searchRange}
												searchLimit={this.state.logSearchLimit}
												setCurRecord={this.setCurRecord}
												/>
										</PrivateRoute>
										<PrivateRoute path='/settings'>
											<SettingsPage 
												searchOptions={this.state.searchParameter.searchOptions} 
												searchOptionChanged={this.handleSearchOptionChanged}
												searchRange={this.state.searchRange}
												searchLimit={this.state.searchLimit}
												logSearchLimit={this.state.logSearchLimit}
												setSearchRange={this.setSearchRange}
												setSearchLimit={this.setSearchLimit}
												setLogSearchLimit={this.setLogSearchLimit}
												/>
											</PrivateRoute>
										<PrivateRoute path='/dialog'>
											<DialogPage 
												curRecord={this.state.curRecord} 
												recordNavigation={this.recordNavigation} 
												setCurRecord={this.setCurRecord}
												/>
										</PrivateRoute>
										<PrivateRoute path='/logging'>
											<LoggingPage 
												curRecord={this.state.curRecord} 
												recordNavigation={this.recordNavigation} 
												setCurRecord={this.setCurRecord}
											/>
										</PrivateRoute>
										<PrivateRoute path='/logHistory'>
											<HistoryPage 
												curRecord={this.state.curRecord} 
												recordNavigation={this.recordNavigation} 
												setCurRecord={this.setCurRecord}
												/>
										</PrivateRoute>
										<PrivateRoute path='/'>
										<SearchPage 
												searchParameter={this.state.searchParameter}
												records={this.state.records}
												curRecord={this.state.curRecord}
												setSearchParameter={this.setSearchParameter}
												setRecords={this.setRecords}
												setCurRecord={this.setCurRecord}
												searchRange={this.state.searchRange}
												searchLimit={this.state.searchLimit}
												/>
										</PrivateRoute>
									</Switch>
									{this.state.showDialog !== undefined ? this.state.showDialog : null }
								</Container>

								<Container className='frame-footer'>
									<Message className='frame-footer-text' ref={ (m) => { this.message = m } } />
									<Text className='frame-space' />
									<Text className='frame-footer-text-right'>{this.state.version !== undefined ? 'V' + this.state.version : null} {'\u00A9 Sympalog Voice Solutions GmbH'}</Text>
								</Container>

							</Container>
							</CompatRouter>

						</Router>

					</Container>

				</AppProvider>

			</Container>

        );

	}
	
	public init = (): Promise<InitResponse | undefined> => {

		return new Promise<InitResponse | undefined>( (resolve) => {

			this.ctx.restAppService.request_get('parameter/init', 'noToken',
				(data) => {

					const response = JSON.parse(data) as InitResponse;
					resolve(response);

				},
				(status, text) => {
					console.log('init failed ' + status + '/' + text);
					resolve(undefined);
				}
			);

		});

	}

	public getParameterTestcases = (): Promise<TestcasesResponse> => {

		return new Promise<TestcasesResponse>(
			(resolve, reject): void => {

				// send request
				this.ctx.restAppService.request_get('parameter/testcases','noToken',
					(data): void => {

						const response = JSON.parse(data) as TestcasesResponse;
						resolve(response);
					},
					(status, text): void => {
						reject('failure ' + status + '/' + text);
					}
				)

			}
		)
	}

	public componentDidMount(): void {

		// initialize parameter 
		this.init().then( (initResponse) => {

			// if success continue with token
			if (initResponse !== undefined) {

				// initialize search options
				const searchOptions: SearchOption[] = initResponse.searchFields.map( (field): SearchOption => {
					return {
						databaseName: field.databaseName,
						displayName: field.displayName,
						viewOption: field.viewOption,
						searchOption: field.searchOption,
						viewActive: field.viewOption === 'N' ? false : true,
						searchActive: field.searchOption === 'N' ? false : true,
						value: ''
					}
				});

				// set version
				this.setState({ version: initResponse.version });

				// check for token
				this.ctx.user.checkToken().then( (result) => {
					if (result) {
		
						console.log('valid token found, user is ' + this.ctx.user.getUsername());
		
						// set searchRange
						const searchRange = this.ctx.user.getUserSetting('SymQuery', 'searchRange');
						if (searchRange !== null) {
							this.setSearchRange(searchRange ?? "yesterday");
						}

						// set searchLimit
						const searchLimit = this.ctx.user.getUserSetting('SymQuery', 'searchLimit');
						if (searchLimit !== null) {
							const limit = Number.parseInt(searchLimit ?? "1000");
							this.setState({ searchLimit: Number.isNaN(limit) ? 0 : limit });
						}

						// set logSearchLimit
						const logSearchLimit = this.ctx.user.getUserSetting('SymQuery', 'logSearchLimit');
						if (logSearchLimit !== null) {
							const limit = Number.parseInt(logSearchLimit ?? "1000");
							this.setState({ logSearchLimit: Number.isNaN(limit) ? 0 : limit });
						}

						// check user setting
						const setting = this.ctx.user.getUserSetting('SymQuery', 'searchOptions');
						if (setting != null) {

							// set active flag of search options
							const userSetting = JSON.parse(setting) as UserSettingSearchField[];
							userSetting.forEach( field => {
								searchOptions
									.filter( option => option.databaseName === field.databaseName )
									.forEach( option => {
										option.viewActive = field.viewActive;
										option.searchActive = field.searchActive;
									});
							});

						}
		
						// set state
						this.setState({ status: '', checkToken: false })
					}
					else {
						console.log('no valid token found');
						this.setState({ status: 'no valid token found', checkToken: false })
					}
				});

				// set new search parameter
				const searchParameter = { ...this.state.searchParameter };
				searchParameter.serverEntries = initResponse.server;
				searchParameter.searchOptions = searchOptions;
				this.setState({ searchParameter });

				// set new logsearch parameter
				const logSearchParameter = { ...this.state.logSearchParameter };
				logSearchParameter.serverEntries = initResponse.server;
				this.setState({ logSearchParameter });

				// get testcases
				this.getParameterTestcases()
					.then( (result) => {
						const testcaseList: string[] = ['<testcase identifier>'];
						result.testcases.forEach( tc => testcaseList.push(tc) );
						this.setState( { testcases: testcaseList });
					})
					.catch(
						(message): void => {
                            this.ctx.app.setMessageText(message);
                        }
					)
		
			}

		});

	}

    public setStatus = (status: string): void => {
		if (this.message !== null) {
			this.message.setMessage(status);
		}
		this.setState({ status }); // use status only to force rerender
    }

	public showLoader(status: boolean): void {
		if (status === true) {
			const dialog: JSX.Element = <MessageLoader />;
			this.setState({ showDialog: dialog });
		}
		else {
			this.setState({ showDialog: undefined });
		}
	}

	public setMessageText(text: string | undefined): void {
		if (text !== undefined) {
			const dialog: JSX.Element = <MessageBox text={text} onClick={ (): void => { this.setState({ showDialog: undefined }) } } />;
			this.setState({ showDialog: dialog });
		}
		else {
			this.setState({ showDialog: undefined });
		}
	}

	public setSearchParameter = (searchParameter: SearchParameter): void => {
		this.setState({ searchParameter });
	}

	public setLogSearchParameter = (logSearchParameter: LogSearchParameter): void => {
		this.setState({ logSearchParameter });
	}

	public setRecords = (records: LogRecord[] | undefined): void => {
		this.setState({ records });
	}

	public setLogList = (logList: Logging[]): void => {
		this.setState({ logList });
	}

	public setCurRecord = (curRecord: LogRecord | undefined): void => {
		this.setState({ curRecord });
	}

	public setSearchRange = (searchRange: string): void => {

		// set search range
		let dateFrom: string;
		let dateTo: string;
		switch (searchRange) {
			case 'today':
				dateFrom = DateUtil.getTodayAsString();
				dateTo = DateUtil.getTodayAsString();
				break;
			case 'yesterday':
				dateFrom = DateUtil.getYesterdayAsString()
				dateTo = DateUtil.getYesterdayAsString();
				break;
			case 'one week':
				{
					const f = DateUtil.getToday();
					f.setDate(f.getDate() - 7);
					const t = DateUtil.getToday();
					dateFrom = DateUtil.getDateAsString(f);
					dateTo = DateUtil.getDateAsString(t);
				}
				break;
			case 'two weeks':
				{
					const f = DateUtil.getToday();
					f.setDate(f.getDate() - 14);
					const t = DateUtil.getToday();
					dateFrom = DateUtil.getDateAsString(f);
					dateTo = DateUtil.getDateAsString(t);
				}
				break;
			default:
				dateFrom = DateUtil.getTodayAsString();
				dateTo = DateUtil.getTodayAsString();
				break;
		}

		// set new search parameter
		const searchParameter = { ...this.state.searchParameter };
		searchParameter.queryDateFrom = dateFrom;
		searchParameter.queryDateTo = dateTo;

		const logSearchParameter = { ...this.state.logSearchParameter };
		logSearchParameter.queryDateFrom = dateFrom;
		logSearchParameter.queryDateTo = dateTo;
		
		// set status
		this.setState({ searchRange, searchParameter, logSearchParameter });

	}

	public setSearchLimit = (searchLimit: number): void => {
		this.setState({ searchLimit });
	}

	public setLogSearchLimit = (logSearchLimit: number): void => {
		this.setState({ logSearchLimit });
	}

	public setCurRecordFromServerSession = (sessionId: string): void => {

		if (this.state.records) {

			// find record
			const newCurRecord = this.state.records.find( record => record.sessionId === sessionId );

			// check if we found a record
			if (newCurRecord) {
				this.setState({ curRecord: newCurRecord });
			}

		}
	}

	public addSidebar = (sbe: SideBarEntry): void => {

		// check if entry does not exist
		if (this.state.sidebar.find( entry => entry.name === sbe.name) === undefined) {
			const newSideBar = [ ...this.state.sidebar ];
			newSideBar.push(sbe);
			this.setState({ sidebar: newSideBar });
		}

	}

	public replaceSidebar = (prev: SideBarEntry, next: SideBarEntry): void => {

		const newSideBar: SideBarEntry[] = [];
		this.state.sidebar.forEach( sbe => {
			newSideBar.push(sbe.link !== prev.link ? sbe : next);
		});
		this.setState({ sidebar: newSideBar });

	}

	public resetSidebar = (curPath: string): void => {

		// reset sidebar but keep an active detail entry
		const newSideBar: SideBarEntry[] = this.createSidebarEntries();
		this.state.sidebar
			.filter(sbe => sbe.name !== 'Search' && sbe.name !== 'LogSearch')
			.filter(sbe => sbe.link === curPath)
			.forEach(sbe => newSideBar.push(sbe) );
		this.setState({ sidebar: newSideBar });

	}

	public nextLeft = (): LogRecord | undefined => {

		// check for undefined
		if (this.state.records === undefined || this.state.curRecord === undefined) {
			return undefined;
		}

		// get (filtered) list
		const filteredRecords = this.filterRecords(this.state.records);

		// find entry in filtered list with lesser sessionId
		for (let x = filteredRecords.length - 1; x >= 0; x--) {
			if (filteredRecords[x].sessionId < this.state.curRecord.sessionId) {
				return filteredRecords[x];
			}
		}

		return undefined;

	}

	public nextRight = (): LogRecord | undefined => {

		// check for undefined
		if (this.state.records === undefined || this.state.curRecord === undefined) {
			return undefined;
		}

		// get (filtered) list
		const filteredRecords = this.filterRecords(this.state.records);

		// find entry in filtered list with higher sessionId
		for (let x = 0; x < filteredRecords.length; x++) {
			if (filteredRecords[x].sessionId > this.state.curRecord.sessionId) {
				return filteredRecords[x];
			}
		}

		return undefined;

	}

	private filterRecords = (records: LogRecord[]): LogRecord[] => {

		return (
			records
				.filter(record => {
					let valid = true;
					this.state.searchParameter.filterMap.forEach( (value, key): void => {
						if (record[key] && record[key] !== value) {
							valid = false;
						}
					})
					return valid;
				})
		);

	}

	public getTestcaseList = (): string[] => {
		return this.state.testcases;
	}

	public handleLogin = (result: boolean): void => {

		if (result) {

			// set searchRange
			const searchRange = this.ctx.user.getUserSetting('SymQuery', 'searchRange');
			if (searchRange !== null) {
				this.setSearchRange(searchRange ?? 'yesterday');
			}

			// set searchLimit
			const searchLimit = this.ctx.user.getUserSetting('SymQuery', 'searchLimit');
			if (searchLimit !== null) {
				const limit = Number.parseInt(searchLimit ?? '1000');
				this.setState({ searchLimit: Number.isNaN(limit) ? 0 : limit });
			}

			// set search options to user setting
			const setting = this.ctx.user.getUserSetting('SymQuery', 'searchOptions');
			if (setting != null) {

				const searchOptions = [...this.state.searchParameter.searchOptions];

				// set active flag of search options
				const userSetting = JSON.parse(setting) as UserSettingSearchField[];
				userSetting.forEach( field => {
					searchOptions
						.filter( option => option.databaseName === field.databaseName )
						.forEach( option => {
							option.viewActive = field.viewActive;
							option.searchActive = field.searchActive;
						});
				});

				// set new search parameter
				const searchParameter = { ...this.state.searchParameter };
				searchParameter.searchOptions = searchOptions;
				this.setState({ searchParameter });

			}

		}
	}


	public handleLogout = (): void => {
        this.ctx.user.logout().then( () => {
			this.setState( { status: '' } );
		});
	}

	public handleSearchOptionChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
		
		const target = event.target;
		const name = target.dataset.name;
		
		if (name) {

			let viewOption = true;
			if (name.startsWith('s_')) {
				viewOption = false;
			}

			const searchOptions: SearchOption[] = [];
			this.state.searchParameter.searchOptions.forEach( searchOption => {

				// create new search options
				if (searchOption.databaseName !== name.substring(2) ) {
					searchOptions.push( { ...searchOption } );
				}
				else {
					if (viewOption) {
						searchOptions.push( { ...searchOption, viewActive: !searchOption.viewActive } );
					}
					else {
						searchOptions.push( { ...searchOption, searchActive: !searchOption.searchActive } );
					}
				}

			});

			// set new search parameter
			const searchParameter = { ...this.state.searchParameter };
			searchParameter.searchOptions = searchOptions;
			this.setState({ searchParameter });

			// set user settings
			const initSearchOptions: UserSettingSearchField[] = searchOptions.map( (option): UserSettingSearchField => { 
				return {
					databaseName: option.databaseName,
					viewActive: option.viewActive,
					searchActive: option.searchActive
				}
			} );
			this.ctx.user.setUserSetting('SymQuery', 'searchOptions', JSON.stringify(initSearchOptions));

		}

	}

	private createSidebarEntries(): SideBarEntry[] {

		const sideBar: SideBarEntry[] = [ 
			{ name: 'Search', link: '/search' }, 
			{ name: 'LogSearch', link: '/logsearch' }, 
		];

		return sideBar;

    }
    
}
